import { useEffect, useState } from "react";
import { Platform, Pressable, Text, View, type TextStyle, type ViewStyle } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Svg, { Path } from "react-native-svg";

import { COLORS } from "../../lib/brand";
import { type PaneKey } from "../../lib/brand-routes";

export type DemoKey = "loading" | "standard" | "notification" | "adminReview" | "adminSearch" | "viewAs";
export type DeviceMode = "phone" | "desktop";

export const HEADER_BG = "#1A1A1A";
export const PAGE_BG = "#141414";
const HEADER_BORDER = "rgba(181,135,58,0.55)";
const BUTTON_BORDER = "rgba(247,244,238,0.14)";
const BUTTON_HOVER_BG = "rgba(247,244,238,0.06)";
const TEXT_DIM = "rgba(247,244,238,0.7)";
const AMBER = COLORS.AMBER.hex;
const AMBER_BG_ACTIVE = "rgba(181,135,58,0.18)";
const AMBER_BORDER_ACTIVE = "rgba(181,135,58,0.55)";

const PANES: readonly PaneKey[] = ["General", "Student", "Client", "Admin", "Judge"] as const;

export function HeaderBar({
  pane,
  onPaneChange,
  demo,
  onDemoSelect,
  device,
  onDeviceChange,
  onBrandingOpen,
  onLogoOpen,
}: {
  pane: PaneKey;
  onPaneChange: (next: PaneKey) => void;
  demo: DemoKey | null;
  onDemoSelect: (next: DemoKey) => void;
  device: DeviceMode;
  onDeviceChange: (next: DeviceMode) => void;
  onBrandingOpen: () => void;
  onLogoOpen: () => void;
}) {
  return (
    <View
      style={{
        backgroundColor: PAGE_BG,
        paddingHorizontal: 20,
        paddingTop: 0,
        paddingBottom: 10,
        zIndex: 100,
        ...(Platform.OS === "web"
          ? ({ position: "sticky", top: 0 } as unknown as ViewStyle)
          : null),
      }}
    >
      <View
        style={{
          backgroundColor: HEADER_BG,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderLeftWidth: 1,
          borderRightWidth: 1,
          borderBottomWidth: 1,
          borderTopWidth: 0,
          borderColor: HEADER_BORDER,
          paddingHorizontal: 14,
          paddingVertical: 6,
          flexDirection: "row",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* LEFT cluster */}
        <View style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
          <LogoButton onPress={onLogoOpen} />
          <BrandingButton onPress={onBrandingOpen} />
          <DeviceToggle device={device} onChange={onDeviceChange} />
        </View>

        {/* spacer */}
        <View style={{ flex: 1 }} />

        {/* RIGHT cluster */}
        <View style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
          <PillButton
            active={demo === "loading"}
            onPress={() => onDemoSelect("loading")}
            icon={<LoadingIcon active={demo === "loading"} />}
            label="Loading Screen"
          />
          <ViewOverlayDropdown demo={demo} onSelect={onDemoSelect} />
        </View>

        {/* CENTER (absolutely positioned so it stays truly centered) */}
        <View
          pointerEvents="box-none"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PaneSelector value={pane} onChange={onPaneChange} />
        </View>
      </View>
    </View>
  );
}

/* ─────────────────────── Logo button ─────────────────────── */

function LogoButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 7,
          paddingHorizontal: 14,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: BUTTON_BORDER,
          height: 36,
        },
        Platform.OS === "web" && hovered
          ? ({ backgroundColor: BUTTON_HOVER_BG, borderColor: AMBER_BORDER_ACTIVE } as ViewStyle)
          : null,
      ]}
    >
      <SunMarkGlyph color={AMBER} />
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        Logos
      </Text>
    </Pressable>
  );
}

function SunMarkGlyph({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path d="M7 0.5 A 6.5 6.5 0 1 1 7 13.5 A 6.5 6.5 0 1 1 7 0.5 Z" stroke={COLORS.SAGE.hex} strokeWidth={0.8} fill="none" />
      <Path d="M7 2.6 A 4.4 4.4 0 1 1 7 11.4 A 4.4 4.4 0 1 1 7 2.6 Z" stroke={color} strokeWidth={1} fill="none" />
      <Path d="M7 5.5 A 1.5 1.5 0 1 1 7 8.5 A 1.5 1.5 0 1 1 7 5.5 Z" fill={color} />
    </Svg>
  );
}

/* ─────────────────────── Branding button (now with cursor icon) ─────────────────────── */

function BrandingButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 7,
          paddingHorizontal: 14,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: BUTTON_BORDER,
          height: 36,
        },
        Platform.OS === "web" && hovered
          ? ({ backgroundColor: BUTTON_HOVER_BG, borderColor: AMBER_BORDER_ACTIVE } as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        UI Flow
      </Text>
      <CursorIcon color={AMBER} />
    </Pressable>
  );
}

/* ─────────────────────── Device toggle ─────────────────────── */

function DeviceToggle({
  device,
  onChange,
}: {
  device: DeviceMode;
  onChange: (next: DeviceMode) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        borderRadius: 999,
        borderWidth: 1,
        borderColor: BUTTON_BORDER,
        padding: 3,
        gap: 2,
      }}
    >
      <DeviceButton
        active={device === "phone"}
        onPress={() => onChange("phone")}
        icon={<PhoneIcon active={device === "phone"} />}
      />
      <DeviceButton
        active={device === "desktop"}
        onPress={() => onChange("desktop")}
        icon={<DesktopIcon active={device === "desktop"} />}
      />
    </View>
  );
}

function DeviceButton({
  active,
  onPress,
  icon,
}: {
  active: boolean;
  onPress: () => void;
  icon: React.ReactNode;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          width: 30,
          height: 30,
          borderRadius: 999,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: active ? AMBER_BG_ACTIVE : "transparent",
          borderWidth: 1,
          borderColor: active ? AMBER_BORDER_ACTIVE : "transparent",
        },
        Platform.OS === "web" && hovered && !active
          ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
          : null,
      ]}
    >
      {icon}
    </Pressable>
  );
}

/* ─────────────────────── View Overlay dropdown ─────────────────────── */

function ViewOverlayDropdown({
  demo,
  onSelect,
}: {
  demo: DemoKey | null;
  onSelect: (next: DemoKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const isActive =
    demo === "standard" ||
    demo === "notification" ||
    demo === "adminReview" ||
    demo === "adminSearch" ||
    demo === "viewAs";

  const choose = (next: DemoKey) => {
    setOpen(false);
    onSelect(next);
  };

  return (
    <View style={{ position: "relative" }}>
      <Pressable
        accessibilityRole="button"
        onPress={() => setOpen((o) => !o)}
        style={({ hovered }) => [
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingVertical: 7,
            paddingHorizontal: 14,
            borderRadius: 999,
            borderWidth: 1,
            height: 36,
            backgroundColor: isActive ? AMBER_BG_ACTIVE : "transparent",
            borderColor: isActive ? AMBER_BORDER_ACTIVE : BUTTON_BORDER,
          },
          Platform.OS === "web" && hovered && !isActive
            ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
            : null,
        ]}
      >
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: isActive ? AMBER : TEXT_DIM,
          }}
        >
          View Overlay
        </Text>
        <ChevronDown color={isActive ? AMBER : TEXT_DIM} />
      </Pressable>

      {open ? (
        <>
          {/* Click-outside backdrop */}
          <Pressable
            onPress={() => setOpen(false)}
            style={[
              {
                position: Platform.OS === "web" ? ("fixed" as unknown as "absolute") : "absolute",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 199,
              } as ViewStyle,
            ]}
          />
          {/* Dropdown panel */}
          <View
            style={[
              {
                position: "absolute",
                top: 44,
                right: 0,
                zIndex: 200,
                backgroundColor: HEADER_BG,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: BUTTON_BORDER,
                paddingVertical: 6,
                paddingHorizontal: 6,
                minWidth: 200,
                gap: 2,
              },
              Platform.OS === "web"
                ? ({ boxShadow: "0 12px 32px rgba(0,0,0,0.45)" } as unknown as ViewStyle)
                : null,
            ]}
          >
            <DropdownItem
              active={demo === "standard"}
              icon={<OverlayIcon active={demo === "standard"} />}
              label="Standard"
              onPress={() => choose("standard")}
            />
            <DropdownItem
              active={demo === "notification"}
              icon={<BellIcon active={demo === "notification"} />}
              label="Notification"
              onPress={() => choose("notification")}
            />
            <DropdownItem
              active={demo === "adminReview"}
              icon={<OverlayIcon active={demo === "adminReview"} />}
              label="Admin case review"
              onPress={() => choose("adminReview")}
            />
            <DropdownItem
              active={demo === "adminSearch"}
              icon={<OverlayIcon active={demo === "adminSearch"} />}
              label="Admin global search"
              onPress={() => choose("adminSearch")}
            />
            <DropdownItem
              active={demo === "viewAs"}
              icon={<OverlayIcon active={demo === "viewAs"} />}
              label="View-as banner"
              onPress={() => choose("viewAs")}
            />
          </View>
        </>
      ) : null}
    </View>
  );
}

function DropdownItem({
  active,
  icon,
  label,
  onPress,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 9,
          paddingHorizontal: 10,
          borderRadius: 8,
          backgroundColor: active ? AMBER_BG_ACTIVE : "transparent",
          borderWidth: 1,
          borderColor: active ? AMBER_BORDER_ACTIVE : "transparent",
        },
        Platform.OS === "web" && hovered && !active
          ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
          : null,
      ]}
    >
      <View style={{ width: 14, height: 14, alignItems: "center", justifyContent: "center" }}>
        {icon}
      </View>
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: active ? AMBER : TEXT_DIM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────── Pane selector (sliding pill) ─────────────────────── */

const SLOT_WIDTH = 96;
const SELECTOR_PAD = 3;
const SLOT_HEIGHT = 30;

function PaneSelector({
  value,
  onChange,
}: {
  value: PaneKey;
  onChange: (next: PaneKey) => void;
}) {
  const activeIndex = PANES.indexOf(value);
  const indicatorX = useSharedValue(activeIndex * SLOT_WIDTH);

  useEffect(() => {
    indicatorX.value = withTiming(activeIndex * SLOT_WIDTH, {
      duration: 320,
      easing: Easing.bezier(0.32, 0.72, 0, 1),
    });
  }, [activeIndex, indicatorX]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
  }));

  return (
    <View
      style={{
        flexDirection: "row",
        borderRadius: 999,
        padding: SELECTOR_PAD,
        borderWidth: 1,
        borderColor: BUTTON_BORDER,
        position: "relative",
        height: 36,
        backgroundColor: "rgba(0,0,0,0.25)",
      }}
    >
      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            top: SELECTOR_PAD,
            left: SELECTOR_PAD,
            width: SLOT_WIDTH,
            height: SLOT_HEIGHT,
            borderRadius: 999,
            backgroundColor: AMBER_BG_ACTIVE,
            borderWidth: 1,
            borderColor: AMBER_BORDER_ACTIVE,
          },
          indicatorStyle,
        ]}
      />
      {PANES.map((p) => {
        const active = p === value;
        return (
          <Pressable
            key={p}
            accessibilityRole="button"
            onPress={() => onChange(p)}
            style={({ hovered }) => [
              {
                width: SLOT_WIDTH,
                height: SLOT_HEIGHT,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
              },
              Platform.OS === "web" && hovered && !active
                ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
                : null,
              Platform.OS === "web"
                ? ({
                    transitionProperty: "color",
                    transitionDuration: "200ms",
                  } as unknown as ViewStyle)
                : null,
            ]}
          >
            <Text
              style={[
                {
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 11,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: active ? AMBER : TEXT_DIM,
                },
                Platform.OS === "web"
                  ? ({
                      transitionProperty: "color",
                      transitionDuration: "220ms",
                    } as unknown as TextStyle)
                  : null,
              ]}
            >
              {p}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ─────────────────────── Reusable pill button ─────────────────────── */

function PillButton({
  active,
  onPress,
  icon,
  label,
}: {
  active: boolean;
  onPress: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: 7,
          paddingHorizontal: 14,
          borderRadius: 999,
          borderWidth: 1,
          height: 36,
          backgroundColor: active ? AMBER_BG_ACTIVE : "transparent",
          borderColor: active ? AMBER_BORDER_ACTIVE : BUTTON_BORDER,
        },
        Platform.OS === "web" && hovered && !active
          ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
          : null,
      ]}
    >
      <View style={{ width: 14, height: 14, alignItems: "center", justifyContent: "center" }}>
        {icon}
      </View>
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: active ? AMBER : TEXT_DIM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────── Icons ─────────────────────── */

function iconColor(active: boolean) {
  return active ? AMBER : TEXT_DIM;
}

function CursorIcon({ color, size = 13 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M5 3 L14 21 L16 13 L23 11 Z"
        fill={color}
        stroke={color}
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function ChevronDown({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <Path
        d="M2 4 L6 8 L10 4"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function LoadingIcon({ active }: { active: boolean }) {
  const c = iconColor(active);
  return (
    <View
      style={{
        width: 12,
        height: 12,
        borderRadius: 999,
        borderWidth: 1.4,
        borderColor: c,
        borderTopColor: "transparent",
        borderRightColor: "transparent",
      }}
    />
  );
}

function OverlayIcon({ active }: { active: boolean }) {
  const c = iconColor(active);
  return (
    <View
      style={{
        width: 13,
        height: 11,
        borderWidth: 1.4,
        borderColor: c,
        borderRadius: 2.5,
        alignItems: "center",
        paddingTop: 2,
      }}
    >
      <View style={{ width: 6, height: 1.2, backgroundColor: c, borderRadius: 1 }} />
    </View>
  );
}

function BellIcon({ active }: { active: boolean }) {
  const c = iconColor(active);
  return (
    <View style={{ width: 12, height: 14, alignItems: "center", justifyContent: "center" }}>
      <View
        style={{
          width: 10,
          height: 9,
          borderWidth: 1.4,
          borderColor: c,
          borderRadius: 5,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      />
      <View
        style={{
          width: 3,
          height: 2,
          borderWidth: 1.4,
          borderColor: c,
          borderTopWidth: 0,
          borderBottomLeftRadius: 2,
          borderBottomRightRadius: 2,
          marginTop: -0.5,
        }}
      />
    </View>
  );
}

function PhoneIcon({ active }: { active: boolean }) {
  const c = iconColor(active);
  return (
    <View
      style={{
        width: 11,
        height: 16,
        borderWidth: 1.4,
        borderColor: c,
        borderRadius: 2.5,
        alignItems: "center",
        justifyContent: "flex-end",
        paddingBottom: 1.5,
      }}
    >
      <View style={{ width: 3, height: 1.2, backgroundColor: c, borderRadius: 1 }} />
    </View>
  );
}

function DesktopIcon({ active }: { active: boolean }) {
  const c = iconColor(active);
  return (
    <View style={{ width: 16, height: 14, alignItems: "center" }}>
      <View
        style={{
          width: 16,
          height: 10,
          borderWidth: 1.4,
          borderColor: c,
          borderRadius: 2,
        }}
      />
      <View style={{ width: 6, height: 1.4, backgroundColor: c, marginTop: 2, borderRadius: 1 }} />
    </View>
  );
}
